import { useState } from 'react';
import { CheckCircle, AlertCircle, RefreshCw, Loader2 } from 'lucide-react';
import { dataEngineApi } from '../../../services/dataEngineApi';

const formatMoney = (v) => '$' + Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AgingTable({ title, aging, color, rfId }) {
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  if (!aging) return null;
  const b = aging.buckets;
  const hasComparison = aging.comparison && aging.comparison.length > 0;
  const hasCounterparties = aging.top_counterparties && aging.top_counterparties.length > 0;
  const noFinBoardData = aging.our_total === 0 || aging.our_total === null || aging.our_total === undefined;
  const hasMismatch = hasComparison && !aging.match;
  const showSyncButton = noFinBoardData || hasMismatch;

  const handleSyncEntities = async () => {
    if (!rfId) return;
    setSyncing(true);
    setSyncResult(null);
    const allEntityTypes = ['Invoices', 'Bills', 'CreditMemos', 'VendorCredits', 'Payments', 'Purchases'];
    try {
      const statusResp = await dataEngineApi.getEntitySyncStatus(rfId);
      const statuses = {};
      for (const s of (statusResp.data || [])) statuses[s.entityType] = s;

      for (const et of allEntityTypes) {
        const s = statuses[et];
        if (s && s.status === 'COMPLETED') {
          await dataEngineApi.triggerEntityCdc(rfId, et);
        } else {
          await dataEngineApi.triggerEntityFullSync(rfId, et);
        }
      }
      setSyncResult({ success: true, message: 'Entity sync complete. Refreshing...' });
      setTimeout(() => window.location.reload(), 2000);
    } catch (err) {
      setSyncResult({ success: false, message: err.message });
    }
    setSyncing(false);
  };

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-300 mb-2 flex items-center gap-2">
        {title}
        {hasComparison && (
          aging.match
            ? <span className="text-emerald-400 text-[10px] font-normal flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Verified</span>
            : <span className="text-amber-400 text-[10px] font-normal flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Mismatch</span>
        )}
      </h3>
      <div className="bg-gray-800/30 border border-gray-800 rounded-xl p-4">
        {/* Totals */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-gray-500">As of {aging.as_of_date}</span>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-[10px] text-gray-600">QB Aging</div>
              <span className={`text-lg font-semibold font-mono ${color}`}>{formatMoney(b.total)}</span>
            </div>
            {aging.our_total !== null && aging.our_total !== undefined && (
              <>
                <div className="text-gray-700">|</div>
                <div className="text-right">
                  <div className="text-[10px] text-gray-600">FinBoard</div>
                  <span className={`text-lg font-semibold font-mono ${aging.match ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {formatMoney(aging.our_total)}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Bucket comparison table */}
        {hasComparison ? (
          <div className="bg-gray-800/20 rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="px-3 py-1.5 text-left text-[10px] text-gray-500 uppercase">Bucket</th>
                  <th className="px-3 py-1.5 text-right text-[10px] text-gray-500 uppercase">QB</th>
                  <th className="px-3 py-1.5 text-right text-[10px] text-gray-500 uppercase">FinBoard</th>
                  <th className="px-3 py-1.5 text-right text-[10px] text-gray-500 uppercase">Variance</th>
                </tr>
              </thead>
              <tbody>
                {aging.comparison.map(c => (
                  <tr key={c.label} className="border-b border-gray-800/30">
                    <td className="px-3 py-1.5 text-gray-300">{c.label}</td>
                    <td className="px-3 py-1.5 text-right text-gray-400 font-mono">{formatMoney(c.qb_amount)}</td>
                    <td className="px-3 py-1.5 text-right text-gray-400 font-mono">{formatMoney(c.our_amount)}</td>
                    <td className={`px-3 py-1.5 text-right font-mono ${c.match ? 'text-emerald-400' : 'text-red-400'}`}>
                      {c.match ? '✓' : ''} {c.variance >= 0 ? '+' : ''}{formatMoney(c.variance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          /* Fallback: QB-only bucket bars */
          <div className="flex gap-2">
            {[
              { label: 'Current', value: b.current },
              { label: '1-30', value: b.days_1_30 },
              { label: '31-60', value: b.days_31_60 },
              { label: '61-90', value: b.days_61_90 },
              { label: '91+', value: b.days_91_plus },
            ].map(({ label, value }) => {
              const pct = b.total > 0 ? (value / b.total) * 100 : 0;
              return (
                <div key={label} className="flex-1 text-center">
                  <div className="text-[10px] text-gray-500 mb-1">{label}</div>
                  <div className="h-2 bg-gray-800 rounded-full overflow-hidden mb-1">
                    {pct > 0 && <div className={`h-full rounded-full ${value > 0 ? (label === 'Current' ? 'bg-blue-500' : label === '91+' ? 'bg-red-500' : 'bg-amber-500') : ''}`} style={{ width: `${Math.max(pct, 4)}%` }} />}
                  </div>
                  <div className={`text-xs font-mono ${value > 0 ? 'text-gray-300' : 'text-gray-600'}`}>{formatMoney(value)}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* Top counterparties */}
        {hasCounterparties && (
          <div className="mt-3 pt-3 border-t border-gray-800/50">
            <div className="text-[10px] text-gray-600 uppercase tracking-wider mb-1.5">Top Outstanding</div>
            <div className="space-y-1">
              {aging.top_counterparties.map((e, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="text-gray-400 truncate max-w-[200px]">{e.name}</span>
                  <span className="text-gray-300 font-mono">{formatMoney(e.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sync entities button */}
        {showSyncButton && rfId && (
          <div className="mt-3 pt-3 border-t border-gray-800/50 flex items-center gap-2">
            <button
              onClick={handleSyncEntities}
              disabled={syncing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-400 bg-blue-500/10 border border-blue-500/20 rounded-lg hover:bg-blue-500/20 disabled:opacity-40 transition-all"
            >
              {syncing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              {noFinBoardData ? 'Sync Entities' : 'Re-sync Entities'}
            </button>
            <span className="text-[10px] text-gray-600">
              {noFinBoardData ? 'Sync invoices, bills & payments to compare' : 'Re-sync to update comparison'}
            </span>
            {syncResult && (
              <span className={`text-[10px] ml-auto ${syncResult.success ? 'text-emerald-400' : 'text-red-400'}`}>
                {syncResult.message}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
